import { store } from '../core/store.js';
import { refs } from '../core/refs.js';
import { getTemplate, getTemplateOptions } from '../core/templates.js';
import { runCode, RUN_TRIGGERS } from './runner.js';
import { triggerAllHighlights } from './live-reflection.js';
import { syncCustomSelect } from './custom-select.js';

/**
 * Front-my-End — Starter Templates Loader Feature
 * Runtime templates are loaded from lazy static JSON assets.
 */
let templateServices = {
  console: null
};

export function configureTemplateLoader(services = {}) {
  templateServices = {
    ...templateServices,
    ...services
  };
}

export async function loadTemplate(name) {
  const template = await getTemplate(name);
  if (!template) {
    templateServices.console?.append('error', `Template '${name}' could not be loaded.`);
    return;
  }

  setTemplateSelectValue(name);

  // Reset preview route and path so new template starts with a clean slate
  store.set('activePreviewRoute', null);
  store.set('activePreviewPath', 'index.html');

  // Update the reactive store values
  store.set('html', template.html);
  store.set('css', template.css);
  store.set('js', template.js);

  // Sync back to DOM textareas
  if (refs.htmlEditor) refs.htmlEditor.value = template.html;
  if (refs.cssEditor) refs.cssEditor.value = template.css;
  if (refs.jsEditor) refs.jsEditor.value = template.js;

  triggerAllHighlights();

  // Loading a template (or resetting to one) is an explicit run of its code.
  await runCode({ trigger: RUN_TRIGGERS.EXPLICIT });

  templateServices.console?.system(`Loaded template '${name}' successfully.`);
}

export async function resetFiddle() {
  const activeTemplate = refs.menuTemplateSelect?.value || 'counter';
  store.set('activePreviewRoute', null);
  store.set('activePreviewPath', 'index.html');
  await loadTemplate(activeTemplate);

  templateServices.console?.clear();
}

export function setTemplateSelectValue(value) {
  if (refs.menuTemplateSelect) {
    refs.menuTemplateSelect.value = value;
    syncCustomSelect(refs.menuTemplateSelect);
  }
}

/**
 * Dynamically constructs the HTML `<option>` list from the lazy template
 * manifest and injects it into the DOM selects.
 */
export async function populateTemplateSelects() {
  try {
    const options = await getTemplateOptions();
    if (!options || !options.length) {
      console.warn('Template manifest returned zero templates; falling back to basic set.');
      return;
    }

    const optionNodes = options
      .concat({ id: 'empty', label: 'Blank Playground' })
      .map((opt) => new Option(opt.label, opt.id));

    if (refs.menuTemplateSelect) {
      refs.menuTemplateSelect.replaceChildren(...optionNodes.map((option) => option.cloneNode(true)));
    }
  } catch (e) {
    console.error('Failed to populate template selects:', e);
  }
}
